<script lang="ts">
	import CityFeaturesStructure from '$lib/components/city/features/CityFeaturesStructure.svelte';
	import SpotlightItem from '$lib/components/city/features/SpotlightItem.svelte';
	import DiscoveryCard from '$lib/components/ui/DiscoveryCard.svelte';
	import HeroNeutral from '$lib/components/ui/HeroNeutral.svelte';
	import { Importance } from '$lib/features/_other/importance.enum';
	import type { PlaceLocation } from '$lib/features/_other/placeLocation';
	import {
		CITY_FEATURE_TYPE_COLOURS,
		CITY_FEATURE_TYPE_ICON_MAP,
		CITY_FEATURE_TYPE_LABELS,
		CityFeatureType
	} from '$lib/features/cityFeature/cityFeatureType.enum';
	import type { CoverImage } from '$lib/features/image/imageDetails.type';
	import type { UnescoSitePreview } from '$lib/features/unescoSite/unescoSite.type';
	import { FULL_SITE_URL, MAX_SPOTLIGHT_ITEMS } from '$lib/statics';
	import { buildExampleItemsPhrase, findFirstAvailableImage, sortShortestToLongest } from '$lib/utils/general.util';
	import { serializeJsonLd } from '$lib/utils/schema.util';
	import { UrlUtil } from '$lib/utils/url.util';
	import IconClockTimeFourOutline from '~icons/mdi/clock-time-four-outline';
	import IconEarth from '~icons/mdi/earth';
	import type { Output } from './+page.server';

	const featureType: CityFeatureType = CityFeatureType.UNESCO_SITE;

	const { data }: { data: { output: Output } } = $props();

	const output = $derived<Output>(data.output);
	const location = $derived<PlaceLocation>(output.location);
	const spotlightItems = $derived.by<UnescoSitePreview[]>(() => {
		const importantItems = output.items.filter((a) => a.feature.importance === Importance.ESSENTIAL);
		const itemsToFeature = importantItems.length > 0 ? importantItems : output.items;
		return itemsToFeature.slice(0, MAX_SPOTLIGHT_ITEMS);
	});
	const otherItems = $derived<UnescoSitePreview[]>(output.items.filter((a) => !spotlightItems.includes(a)));
	const totalHoursDuration = $derived<number>(output.items.reduce((sum: number, item) => sum + item.durationHours, 0));
	const meta = $derived.by(() => {
		const orderedNames: string[] = sortShortestToLongest(
			[...spotlightItems, ...otherItems].map((item) => item.feature.englishName)
		);
		const shortestName: string = orderedNames[0];
		const firstAvailableImage: CoverImage | undefined = findFirstAvailableImage(output.items);
		const itemsLength = output.items.length;
		const hourPhrase = totalHoursDuration > 0 ? ` over ${totalHoursDuration} ${totalHoursDuration === 1 ? 'hour' : 'hours'}` : '';
		const exampleItemsPhrase: string = buildExampleItemsPhrase({
			names: orderedNames,
			totalItems: orderedNames.length,
			limit: 4
		});
		const description =
			itemsLength === 1
				? `${shortestName} is ${location.city.label}'s UNESCO World Heritage Site — a globally recognised landmark you can explore${hourPhrase ? ` in ${totalHoursDuration} ${totalHoursDuration === 1 ? 'hour' : 'hours'}` : ''}. A must for any ${location.city.label} itinerary.`
				: `Explore ${itemsLength} UNESCO World Heritage Sites in ${location.city.label}${hourPhrase}, ${exampleItemsPhrase}.`;
		const hasMultiple = itemsLength > 1;
		return {
			title: `${location.city.label} UNESCO ${hasMultiple ? 'Sites' : 'Site'}: ${shortestName}${hasMultiple ? ' & More' : ''}`,
			description,
			canonicalUrl: `${FULL_SITE_URL}/${UrlUtil.toCityFeatures({ location, type: featureType }).href}`,
			imageUrl: firstAvailableImage ? UrlUtil.getFullImageUrl(firstAvailableImage) : undefined
		};
	});
	const jsonLdTouristAttractionsCollection = $derived.by(() => {
		const json = data.output.jsonLdTouristAttractionsCollection;
		json.name = meta.title;
		json.description = meta.description;
		return json;
	});
	const combinedJsonLd = $derived([data.output.jsonLdBreadcrumbs, jsonLdTouristAttractionsCollection]);
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={meta.canonicalUrl} />

	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:url" content={meta.canonicalUrl} />
	<meta property="og:image" content={meta.imageUrl} />
	<meta property="og:type" content="website" />

	<meta property="twitter:title" content={meta.title} />
	<meta property="twitter:description" content={meta.description} />
	<meta property="twitter:url" content={meta.canonicalUrl} />
	<meta property="twitter:image" content={meta.imageUrl} />
	<meta property="twitter:card" content="summary_large_image" />

	{@html `<script type="application/ld+json">${serializeJsonLd(combinedJsonLd)}</script>`}
</svelte:head>

<CityFeaturesStructure {location} {spotlightItems} {otherItems} keepExploring={output.keepExploring}>
	{#snippet InfoSectionSnippet()}
		<section
			class="relative mt-8 mb-16 overflow-hidden rounded-3xl border border-base-content/5 bg-base-200/50 p-8 shadow-sm md:p-12"
		>
			<div class="pointer-events-none absolute -top-10 -right-10 text-base-content/5">
				<IconEarth style="width: 16rem; height: 16rem;" />
			</div>

			<div class="relative z-10 max-w-4xl">
				<h2 class="mb-4 text-2xl font-bold tracking-tight text-base-content md:text-3xl">
					What is a UNESCO World Heritage Site?
				</h2>

				<div class="space-y-4 text-lg leading-relaxed font-light text-base-content/80">
					<p>
						UNESCO (The United Nations Educational, Scientific and Cultural Organization) awards this prestigious title to places
						that demonstrate <strong>outstanding universal value to humanity</strong>.
					</p>
					<p>
						Whether it's a breathtaking natural landscape, an ancient monument, or a historically significant city district, these
						landmarks are legally protected by international treaties. Exploring a UNESCO site means stepping into a preserved
						piece of global history deemed crucial to our collective memory.
					</p>
				</div>
			</div>
		</section>
	{/snippet}

	{#snippet HeaderSnippet()}
		{const hasOnlyOneSite = $derived<boolean>(output.items.length === 1)}
		<HeroNeutral
			title={`${CITY_FEATURE_TYPE_LABELS[featureType]} in ${location.city.label}`}
			description={`Step back in time and explore ${hasOnlyOneSite ? 'a' : output.items.length} World Heritage ${output.items.length === 1 ? 'Site' : 'Sites'} recognized for ${hasOnlyOneSite ? 'its' : 'their'} outstanding universal value. To fully immerse yourself in this history, we recommend setting aside ${totalHoursDuration} hours for your journey.`}
			breadcrumbs={data.output.breadcrumbs}
			decorations={{
				hexColour: CITY_FEATURE_TYPE_COLOURS[featureType]
			}}
		/>
	{/snippet}

	{#snippet SpotlightItemSnippet(item: UnescoSitePreview, index: number)}
		<SpotlightItem
			elementId={item.feature.slug}
			feature={item.feature}
			language={data.output.countryLanguage}
			nav={UrlUtil.toCityFeature({ location, type: item.feature.type, featureSlug: item.feature.slug })}
			badge={{ text: `${item.durationHours}h`, Icon: IconClockTimeFourOutline }}
			fact={item.fact}
			isReversed={index % 2 !== 0}
			lazyLoadImage={index >= 2}
		/>
	{/snippet}

	{#snippet OtherItemSnippet(item: UnescoSitePreview)}
		<DiscoveryCard
			headingTag="h3"
			elementId={item.feature.slug}
			englishName={item.feature.englishName}
			nativeName={item.feature.nativeName}
			description={item.feature.description}
			language={data.output.countryLanguage}
			images={item.feature.images}
			nav={UrlUtil.toCityFeature({ location, type: item.feature.type, featureSlug: item.feature.slug })}
			badge={{ text: `${item.durationHours}h`, Icon: IconClockTimeFourOutline }}
			NoImagePlaceholderIcon={CITY_FEATURE_TYPE_ICON_MAP[featureType]}
		/>
	{/snippet}
</CityFeaturesStructure>
